import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    applyPrimaryCssVars,
    buildPrimaryCssVars,
    clearPrimaryCssVars,
    DEFAULT_PRIMARY_COLOR,
    removeStoredPrimaryColor,
    readStoredPrimaryColor,
    updateThemeColorMeta,
    writeStoredPrimaryColor,
} from 'app/core/theme/apply-primary-palette';
import { generatePalette } from 'app/core/theme/generate-palette';

@Injectable({ providedIn: 'root' })
export class ThemePreferenceService {
    private readonly _primaryColor$ = new BehaviorSubject<string>(
        DEFAULT_PRIMARY_COLOR
    );

    readonly primaryColor$ = this._primaryColor$.asObservable();

    /** Apply saved color on startup (or keep built-in default). */
    init(): void {
        const stored = readStoredPrimaryColor();
        if (!stored) {
            this._primaryColor$.next(DEFAULT_PRIMARY_COLOR);
            return;
        }

        applyPrimaryCssVars(stored.cssVars);
        updateThemeColorMeta(stored.primaryHex);
        this._primaryColor$.next(stored.primaryHex);
    }

    get primaryColor(): string {
        return this._primaryColor$.value;
    }

    get isCustomColor(): boolean {
        return readStoredPrimaryColor() !== null;
    }

    setPrimaryColor(hex: string): void {
        const normalized = normalizeHex(hex);
        if (!normalized) {
            return;
        }

        const palette = generatePalette(normalized);
        if (!palette) {
            return;
        }

        const cssVars = buildPrimaryCssVars(palette);
        applyPrimaryCssVars(cssVars);
        writeStoredPrimaryColor({ primaryHex: normalized, cssVars });
        updateThemeColorMeta(normalized);
        this._primaryColor$.next(normalized);
    }

    resetToDefault(): void {
        removeStoredPrimaryColor();
        clearPrimaryCssVars();
        updateThemeColorMeta(DEFAULT_PRIMARY_COLOR);

        const initStyle = document.getElementById('bizcare-primary-color-init');
        initStyle?.remove();

        this._primaryColor$.next(DEFAULT_PRIMARY_COLOR);
    }
}

function normalizeHex(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash.toUpperCase() : null;
}
